// Questline accounts UI: owner setup, sign in, sign up, waiting room, account & devices, admin approvals.
import { levelInfo } from '../shared/game.js';
const app = document.getElementById('app');
const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
const Q = new URLSearchParams(location.search);
const path = location.pathname.replace(/\/+$/, '') || '/login';
// only ever go back to a page on this site
const safeNext = (n) => (n && /^\/(?!\/)[^\\]*$/.test(n) && !/^\/(login|signup|setup|pending)/.test(n) ? n : '/');
const NEXT = safeNext(Q.get('next'));

async function call(url, body, method = body ? 'POST' : 'GET') {
  const res = await fetch(url, { method, headers: { 'Content-Type': 'application/json' }, body: body ? JSON.stringify(body) : undefined, credentials: 'same-origin' });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const e = new Error(data.error || `Something went wrong (${res.status})`);
    e.status = res.status;
    e.data = data;
    throw e;
  }
  return data;
}
function toast(html, kind = '') {
  const t = document.createElement('div');
  t.className = 'toast ' + kind;
  t.innerHTML = html;
  document.getElementById('toasts').appendChild(t);
  setTimeout(() => t.remove(), 3600);
}
const ago = (ts) => {
  if (!ts) return 'never';
  const m = Math.round((Date.now() - ts) / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m} min ago`;
  if (m < 1440) return `${Math.round(m / 60)} h ago`;
  return `${Math.round(m / 1440)} d ago`;
};
const level = (xp) => levelInfo(xp || 0).level;

const STORY = `<aside class="au-story">
  <div class="au-brand"><span class="au-logo">⚔️</span>Questline</div>
  <div class="au-island" aria-hidden="true"><div class="rock"></div><div class="top"></div><div class="tree">🌳</div><div class="hero">🦊</div><div class="flag">🚩</div></div>
  <h1>Your life,<br/>played like a <em>game</em>.</h1>
  <p>Plan your day, finish your plan, and your hero walks forward. Goals become bosses, habits become quests, friends become your party.</p>
  <div class="au-feats">
    <div class="au-feat"><i>🗺️</i>A real road for every day you plan</div>
    <div class="au-feat"><i>🤝</i>An AI partner that keeps you honest</div>
    <div class="au-feat"><i>🏁</i>Races and shared goals with friends</div>
  </div>
</aside>`;
const split = (form) => `<div class="au-split">${STORY}<section class="au-form">${form}</section></div>`;
const pwField = (name, label, auto, hint = '') => `<label class="field"><span>${label}${hint ? `<small>${hint}</small>` : ''}</span><div class="au-pw"><input class="input" type="password" name="${name}" autocomplete="${auto}" required minlength="${auto === 'current-password' ? 1 : 8}" maxlength="200"/><button type="button" class="au-eye" data-eye>Show</button></div>${auto === 'new-password' ? '<div class="au-meter"><i></i></div>' : ''}</label>`;
const errBox = '<div class="au-err" role="alert"></div>';

function wireForm(form, submit) {
  const err = form.querySelector('.au-err');
  form.addEventListener('click', (e) => {
    const b = e.target.closest('[data-eye]');
    if (!b) return;
    const i = b.parentElement.querySelector('input');
    i.type = i.type === 'password' ? 'text' : 'password';
    b.textContent = i.type === 'password' ? 'Show' : 'Hide';
  });
  form.querySelectorAll('input[autocomplete=new-password]').forEach((i) =>
    i.addEventListener('input', () => {
      const v = i.value;
      const score = (v.length >= 8) + (v.length >= 12) + (/[A-Z]/.test(v) && /[a-z]/.test(v)) + /\d/.test(v) + /[^A-Za-z0-9]/.test(v);
      const bar = i.closest('.field').querySelector('.au-meter i');
      if (bar) (bar.style.width = Math.min(100, (score / 5) * 100) + '%'), (bar.style.background = ['#ff5d73', '#ff5d73', '#ff9a3d', '#ffc23d', '#35e09a', '#35e09a'][score]);
    }),
  );
  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    const btn = form.querySelector('button[type=submit]');
    const label = btn.innerHTML;
    btn.disabled = true;
    btn.innerHTML = '<span class="au-spin" style="width:18px;height:18px;border-width:2px"></span>';
    err.classList.remove('on');
    try {
      await submit(Object.fromEntries(new FormData(form).entries()), form);
    } catch (x) {
      err.textContent = x.message;
      err.classList.add('on');
    } finally {
      btn.disabled = false;
      btn.innerHTML = label;
    }
  });
}

// ---------- views ----------
function setupView(me) {
  const h = me.setup.hero;
  app.innerHTML = split(`<form novalidate>
    <h2>Create the owner account</h2>
    <div class="au-sub">You're the first one here, so this account runs the place: you approve everyone who signs up.</div>
    ${h ? `<div class="au-hero-chip"><span class="au-av" style="--a:${esc(h.color)}">${esc(h.avatar || '🦊')}</span><div style="text-align:left"><b>${esc(h.name)}</b> · level ${level(h.xp)}<div class="dim" style="font-size:13px;font-weight:600">Your hero and all of its progress will be linked to this account.</div></div></div>` : ''}
    ${errBox}
    <label class="field"><span>Your name</span><input class="input" name="name" maxlength="40" autocomplete="name" value="${esc(h?.name || '')}" required/></label>
    <label class="field"><span>Username</span><input class="input" name="username" maxlength="24" autocomplete="username" autocapitalize="none" spellcheck="false" required pattern="[A-Za-z0-9][A-Za-z0-9_.\\-]{2,23}"/></label>
    <label class="field"><span>Email<small>optional · you can sign in with it</small></span><input class="input" type="email" name="email" maxlength="120" autocomplete="email"/></label>
    ${pwField('password', 'Password', 'new-password', 'at least 8 characters')}
    ${me.setup.codeRequired ? `<label class="field"><span>Setup code<small>in data/setup-code.txt on the server</small></span><input class="input" name="setupCode" maxlength="12" autocapitalize="characters" spellcheck="false" required/></label>` : ''}
    <label class="au-check"><input type="checkbox" name="remember" checked/> Remember this device</label>
    <button class="btn primary block" type="submit">Create owner account →</button>
  </form>`);
  wireForm(app.querySelector('form'), async (f) => {
    await call('/api/auth/setup', { ...f, remember: !!f.remember });
    location.href = '/';
  });
}

function loginView(me) {
  const invited = Q.get('invite');
  app.innerHTML = split(`<form novalidate>
    <h2>Welcome back 👋</h2>
    <div class="au-sub">Sign in to pick up your quest where you left it.</div>
    ${me.trustedLocal && me.player ? `<div class="au-note">This computer is trusted as <b>${esc(me.player.name)}</b>. <a class="au-link" href="${esc(NEXT)}">Continue as ${esc(me.player.name)} →</a><br/><span class="dim" style="font-size:13px">Or sign in below with another account.</span></div>` : ''}
    ${Q.get('bye') ? '<div class="au-note">👋 You’re signed out.</div>' : ''}
    ${errBox}
    <label class="field"><span>Username or email</span><input class="input" name="login" maxlength="120" autocomplete="username" autocapitalize="none" spellcheck="false" required autofocus/></label>
    ${pwField('password', 'Password', 'current-password')}
    <label class="au-check"><input type="checkbox" name="remember" checked/> Remember this device</label>
    <button class="btn primary block" type="submit">Sign in →</button>
    <div class="au-alt">New here? <a href="/signup${invited ? '?invite=' + encodeURIComponent(invited) : ''}">Create an account</a></div>
  </form>`);
  wireForm(app.querySelector('form'), async (f) => {
    const r = await call('/api/auth/login', { login: f.login, password: f.password, remember: !!f.remember });
    if (r.user.status === 'pending') return boot();
    location.href = NEXT;
  });
}

async function signupView() {
  const code = (Q.get('invite') || '').toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 10);
  let inv = null;
  if (code) inv = await call('/api/invite/' + code).catch(() => null);
  app.innerHTML = split(`<form novalidate>
    <h2>${inv ? `${esc(inv.byAvatar || '🎉')} ${esc(inv.by)} invited you!` : 'Start your adventure ✨'}</h2>
    <div class="au-sub">${inv?.race ? `Join the race <b>“${esc(inv.race.name)}”</b> (${inv.race.days} days, ${inv.race.members} racers). ` : ''}Create your account — the owner approves new players, then your hero is ready.</div>
    ${errBox}
    <label class="field"><span>Your name</span><input class="input" name="name" maxlength="40" autocomplete="nickname" required autofocus placeholder="What should we call you?"/></label>
    <label class="field"><span>Username</span><input class="input" name="username" maxlength="24" autocomplete="username" autocapitalize="none" spellcheck="false" required/></label>
    <label class="field"><span>Email<small>optional</small></span><input class="input" type="email" name="email" maxlength="120" autocomplete="email"/></label>
    ${pwField('password', 'Password', 'new-password', 'at least 8 characters')}
    <label class="au-check"><input type="checkbox" name="remember" checked/> Remember this device</label>
    <button class="btn primary block" type="submit">Create my account →</button>
    <div class="au-alt">Already have one? <a href="/login">Sign in</a></div>
  </form>`);
  wireForm(app.querySelector('form'), async (f) => {
    await call('/api/auth/signup', { ...f, remember: !!f.remember, invite: code || undefined });
    boot();
  });
}

let pollT = null;
function pendingView(me) {
  const u = me.user;
  app.innerHTML = `<div class="au-solo">
    <div class="au-wait"><span>⏳</span></div>
    <h2>You're on the list, ${esc(u.name)}!</h2>
    <p>${esc(me.ownerName)} just got a notification about your sign-up. As soon as you're approved, your hero is created and this page opens Questline by itself.</p>
    <div class="au-steps">
      <div class="au-step done"><b>✓</b>Account created · @${esc(u.username)}</div>
      <div class="au-step now"><b>2</b>Waiting for ${esc(me.ownerName)} to let you in</div>
      <div class="au-step"><b>3</b>Meet your hero and plan your first day</div>
    </div>
    <div class="au-btn-row" style="justify-content:center;margin-top:18px"><button class="btn sm" data-check>🔄 Check now</button><button class="btn sm ghost" data-logout>Sign out</button></div>
  </div>`;
  app.querySelector('[data-check]').onclick = () => boot();
  app.querySelector('[data-logout]').onclick = logout;
  clearTimeout(pollT);
  pollT = setTimeout(async () => {
    const m = await call('/api/auth/me').catch(() => null);
    if (m?.user?.status === 'approved') location.href = NEXT;
    else if (m?.user?.status === 'pending') pendingView(m);
    else boot();
  }, 8000);
}

function blockedView(me) {
  app.innerHTML = `<div class="au-solo"><div style="font-size:56px">🚧</div><h2>${me.user.status === 'blocked' ? 'This account is blocked' : 'Not approved'}</h2><p>${me.user.status === 'blocked' ? 'Ask the owner if you think this is a mistake.' : 'The owner didn’t approve this sign-up.'}</p><button class="btn" data-logout>Sign out</button></div>`;
  app.querySelector('[data-logout]').onclick = logout;
}
function demoView() {
  app.innerHTML = `<div class="au-solo"><div style="font-size:56px">🧪</div><h2>This is the demo</h2><p>No account needed — you're signed in as the demo hero.</p><a class="btn primary" href="/">Open the demo →</a></div>`;
}

async function logout() {
  await call('/api/auth/logout', {}).catch(() => {});
  location.href = '/login?bye=1';
}

// ---------- account ----------
const APP_IC = { mac: '💻', windows: '🖥️', android: '📱', ios: '📱' };
const APP_NAME = { mac: 'Mac', windows: 'Windows', android: 'Android', ios: 'iPhone' };
// native app devices: "Questline for Mac 1.4.0 (12) · macOS 26"
const appLine = (s) => (s.kind === 'app' ? `<span class="pill" style="--a:#8b5cff">Questline for ${esc(APP_NAME[s.platform] || s.platform)}${s.appVersion ? ' ' + esc(s.appVersion) : ''}${s.build != null ? ` (${esc(s.build)})` : ''}</span>` : '');
const DEV_IC = (s) => (s.kind === 'app' ? APP_IC[s.platform] || '📱' : s.kind === 'device' ? '🔑' : /iPhone|Android/.test(s.label) ? '📱' : /iPad/.test(s.label) ? '📲' : /Windows|Linux/.test(s.label) ? '🖥️' : '💻');
async function accountView(me) {
  const u = me.user;
  const pl = me.player;
  app.innerHTML = `<div class="au-page">
    <div class="au-top"><span class="au-av" style="--a:${esc(pl?.color || '#8b5cff')};width:56px;height:56px;font-size:28px">${esc(pl?.avatar || '🦊')}</span>
      <div class="grow"><h1>${esc(u.name)}</h1><div class="sub">@${esc(u.username)}${u.email ? ' · ' + esc(u.email) : ''}${u.role === 'admin' ? ' · <span class="pill" style="--a:#ffc23d">👑 Admin</span>' : ''}</div></div>
      <div class="au-btn-row"><a class="btn sm" href="/">← Back to Questline</a>${u.role === 'admin' ? '<a class="btn sm" href="/admin">🛡️ Admin</a>' : ''}</div></div>
    ${me.trustedLocal ? '<div class="au-note">🖥️ This computer is trusted as the owner (QUESTLINE_TRUST_LOCAL). Signing out here only ends browser sessions — local apps stay signed in.</div>' : ''}
    <div class="au-cards">
      <section class="au-card wide"><h3>📱 Devices</h3><p class="hint">Everywhere you're signed in. Remove anything you don't recognise.</p><div id="devs"><div class="au-loading" style="padding:20px"><span class="au-spin"></span></div></div>
        <div class="au-btn-row" style="margin-top:12px"><button class="btn sm danger" data-all>Sign out everywhere</button>${me.trustedLocal ? '' : '<button class="btn sm" data-logout>Sign out of this device</button>'}</div></section>
      <section class="au-card"><h3>🔒 Change password</h3><p class="hint">Every other device is signed out when you change it.</p>
        <form id="pwf" novalidate>${errBox}${pwField('current', 'Current password', 'current-password')}${pwField('next', 'New password', 'new-password', 'at least 8 characters')}<button class="btn primary" type="submit">Update password</button></form></section>
      <section class="au-card"><h3>👤 Profile</h3><p class="hint">How you sign in. Your hero's name, avatar and look live in Questline → Profile.</p>
        <form id="prf" novalidate>${errBox}<label class="field"><span>Name</span><input class="input" name="name" maxlength="40" value="${esc(u.name)}"/></label><label class="field"><span>Email</span><input class="input" type="email" name="email" maxlength="120" value="${esc(u.email || '')}"/></label><button class="btn" type="submit">Save</button></form></section>
      ${u.role === 'admin' ? `<section class="au-card wide"><h3>🔑 Add a device key (old way)</h3><p class="hint">The Questline apps for Mac, Windows, Android and iPhone now sign in by themselves — use this only for something that can't (a kiosk screen, a script). Creates a long-lived key; it stays signed in until you remove it from Devices.</p>
        <form id="dvf" class="au-btn-row" novalidate>${errBox}<input class="input" name="label" maxlength="60" placeholder="This Mac (wallpaper + menu bar)" style="flex:1;min-width:200px"/><button class="btn primary" type="submit">Create device key</button></form><div id="dvOut"></div></section>` : ''}
    </div></div>`;
  const devs = app.querySelector('#devs');
  async function loadDevs() {
    const { sessions } = await call('/api/auth/sessions');
    devs.innerHTML = sessions.length
      ? sessions.map((s) => `<div class="au-dev ${s.current ? 'me' : ''}"><span class="ic">${DEV_IC(s)}</span><div class="grow"><div class="t">${esc(s.label)}${s.current ? '<span class="pill" style="--a:#35e09a">this device</span>' : ''}${s.kind === 'device' ? '<span class="pill" style="--a:#ffc23d">device key</span>' : ''}${appLine(s)}</div><div class="m">${s.kind === 'app' && (s.os || s.model) ? esc([s.os, s.model].filter(Boolean).join(' · ')) + ' · ' : ''}Active ${ago(s.lastSeen)} · since ${new Date(s.createdAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}${s.ip ? ' · ' + esc(s.ip) : ''}${s.kind === 'browser' ? (s.remember ? ' · remembered' : ' · until the browser closes') : ''}</div></div><button class="btn sm danger" data-rev="${esc(s.id)}">${s.current ? 'Sign out' : 'Remove'}</button></div>`).join('')
      : '<div class="au-empty" style="padding:20px">No signed-in devices.</div>';
  }
  loadDevs().catch((e) => (devs.textContent = e.message));
  devs.addEventListener('click', async (e) => {
    const b = e.target.closest('[data-rev]');
    if (!b) return;
    const r = await call('/api/auth/sessions/revoke', { id: b.dataset.rev }).catch((x) => toast('⚠️ ' + esc(x.message), 'err'));
    if (r?.current && !me.trustedLocal) return (location.href = '/login?bye=1');
    toast('✓ Device removed', 'gold');
    loadDevs();
  });
  app.querySelector('[data-all]').onclick = async () => {
    if (!confirm('Sign out on every device (including this one)?')) return;
    await call('/api/auth/logout-all', {});
    location.href = me.trustedLocal ? '/account' : '/login?bye=1';
  };
  app.querySelector('[data-logout]')?.addEventListener('click', logout);
  wireForm(app.querySelector('#pwf'), async (f, form) => {
    await call('/api/auth/password', f);
    form.reset();
    toast('🔒 Password updated · other devices signed out', 'gold');
    loadDevs();
  });
  wireForm(app.querySelector('#prf'), async (f) => {
    await call('/api/auth/profile', f);
    toast('✓ Saved', 'gold');
  });
  const dvf = app.querySelector('#dvf');
  if (dvf)
    wireForm(dvf, async (f) => {
      const { token } = await call('/api/auth/device', f);
      const base = location.origin;
      app.querySelector('#dvOut').innerHTML = `<div class="au-note" style="margin-top:12px">Copy it now — it's shown only once.</div>
        <div class="field"><span>Wallpaper link</span><div class="au-token">${esc(base)}/wallpaper?style=journey&amp;token=${esc(token)}</div></div>
        <div class="field"><span>Menu-bar panel / app link</span><div class="au-token">${esc(base)}/quick?token=${esc(token)}</div></div>
        <div class="field"><span>Key only (Authorization: Bearer …)</span><div class="au-token">${esc(token)}</div></div>`;
      dvf.reset();
      loadDevs();
    });
}

// ---------- admin ----------
let adminTab = 'pending';
async function adminView() {
  if (adminTab === 'apps') return appsView();
  if (adminTab === 'log') return logView();
  const { users, players } = await call('/api/admin/users');
  const groups = { pending: users.filter((u) => u.status === 'pending'), approved: users.filter((u) => u.status === 'approved'), blocked: users.filter((u) => u.status === 'blocked' || u.status === 'denied') };
  const free = players.filter((p) => !p.linkedTo);
  const card = (u) => {
    const tag = u.owner ? '<span class="pill" style="--a:#ffc23d">👑 Owner</span>' : u.role === 'admin' ? '<span class="pill" style="--a:#ffc23d">Admin</span>' : '';
    let acts = '';
    if (u.owner) acts = '';
    else if (u.status === 'pending' || u.status === 'denied' || u.status === 'blocked') {
      acts = `<select class="input" data-link="${esc(u.id)}" aria-label="Hero for ${esc(u.name)}"><option value="new">✨ Create a fresh hero “${esc(u.name)}”</option>${free.map((p) => `<option value="${esc(p.id)}">🔗 Link to existing hero: ${esc(p.avatar || '')} ${esc(p.name)} (Lv ${level(p.xp)})</option>`).join('')}</select>
        <button class="btn sm primary" data-op="approve" data-id="${esc(u.id)}">✓ Approve</button>${u.status === 'pending' ? `<button class="btn sm" data-op="deny" data-id="${esc(u.id)}">Deny</button><button class="btn sm danger" data-op="block" data-id="${esc(u.id)}">Block</button>` : `<button class="btn sm danger" data-op="delete" data-id="${esc(u.id)}">Delete</button>`}`;
    } else {
      acts = `<button class="btn sm" data-op="${u.role === 'admin' ? 'unadmin' : 'admin'}" data-id="${esc(u.id)}">${u.role === 'admin' ? 'Remove admin' : 'Make admin'}</button><button class="btn sm danger" data-op="block" data-id="${esc(u.id)}">Block</button>`;
    }
    return `<div class="au-user"><span class="au-av">${u.status === 'pending' ? '⏳' : u.status === 'approved' ? '🙂' : '🚫'}</span>
      <div class="who"><b>${esc(u.name)}</b> ${tag}<div class="meta"><span>@${esc(u.username)}</span>${u.email ? `<span>${esc(u.email)}</span>` : ''}<span>joined ${ago(u.createdAt)}</span>${u.playerName ? `<span>🧙 ${esc(u.playerName)}</span>` : ''}${u.invite ? `<span>🎟️ invite ${esc(u.invite)}</span>` : ''}${u.status === 'approved' ? `<span>${u.sessions} device${u.sessions === 1 ? '' : 's'}</span><span>last sign-in ${ago(u.lastLoginAt)}</span>` : ''}${u.status === 'denied' ? '<span>denied</span>' : ''}</div></div>
      <div class="acts">${acts}</div></div>`;
  };
  const list = groups[adminTab];
  app.innerHTML = `<div class="au-page">
    <div class="au-top"><span class="au-av" style="width:56px;height:56px;font-size:28px">🛡️</span><div class="grow"><h1>Admin</h1><div class="sub">Who gets into your Questline</div></div><div class="au-btn-row"><a class="btn sm" href="/">← Questline</a><a class="btn sm" href="/account">🔐 Account</a></div></div>
    ${adminTabs(groups)}
    <div class="au-list">${list.length ? list.map(card).join('') : `<div class="au-empty"><div class="big">${adminTab === 'pending' ? '🌤️' : adminTab === 'approved' ? '🧭' : '🕊️'}</div>${adminTab === 'pending' ? 'Nobody is waiting. Share an invite link from Race → Invite, or send people to /signup.' : adminTab === 'approved' ? 'No players yet.' : 'Nobody is blocked.'}</div>`}</div>
  </div>`;
  app.querySelector('.au-tabs').onclick = (e) => {
    const b = e.target.closest('[data-tab]');
    if (b) (adminTab = b.dataset.tab), adminView();
  };
  app.querySelector('.au-list').onclick = async (e) => {
    const b = e.target.closest('[data-op]');
    if (!b) return;
    const op = b.dataset.op;
    if (['block', 'delete', 'deny'].includes(op) && !confirm(`${op[0].toUpperCase() + op.slice(1)} this account?`)) return;
    const player = app.querySelector(`[data-link="${CSS.escape(b.dataset.id)}"]`)?.value;
    b.disabled = true;
    try {
      await call('/api/admin/users', { id: b.dataset.id, op, player });
      toast(op === 'approve' ? '🎉 Approved — their hero is ready' : '✓ Done', 'gold');
    } catch (x) {
      toast('⚠️ ' + esc(x.message), 'err');
    }
    adminView();
  };
}

const adminTabs = (groups) => `<div class="au-tabs" role="tablist">${[['pending', '⏳ Waiting'], ['approved', '✅ Players'], ['blocked', '🚫 Blocked'], ['apps', '📦 Apps'], ['log', '🔐 Security log']].map(([k, l]) => `<button role="tab" class="${adminTab === k ? 'on' : ''}" data-tab="${k}">${l}${groups?.[k]?.length ? `<span class="n">${groups[k].length}</span>` : ''}</button>`).join('')}</div>`;
const adminTop = `<div class="au-top"><span class="au-av" style="width:56px;height:56px;font-size:28px">🛡️</span><div class="grow"><h1>Admin</h1><div class="sub">Who gets into your Questline</div></div><div class="au-btn-row"><a class="btn sm" href="/">← Questline</a><a class="btn sm" href="/account">🔐 Account</a></div></div>`;
const fmtSize = (n) => (n >= 1048576 ? (n / 1048576).toFixed(1) + ' MB' : n >= 1024 ? Math.round(n / 1024) + ' KB' : (n || 0) + ' B');

// ---------- admin → 🔐 Security log (owner only): sign-ins, failures, password changes, admin actions (docs/SECURITY.md) ----------
const LOG_EV = { 'login.ok': ['✅', 'Signed in'], 'login.fail': ['❌', 'Wrong password'], 'login.throttled': ['⏳', 'Sign-in slowed down'], 'login.guessing': ['🚨', 'Password guessing'], 'refresh.reuse': ['🚨', 'Stolen app key used'], 'password.change': ['🔑', 'Password changed'], 'password.fail': ['❌', 'Wrong current password'], 'signup': ['🆕', 'Sign-up'], 'setup': ['🛠️', 'Owner set up'], 'setup.fail': ['❌', 'Wrong setup code'], 'logout.all': ['🚪', 'Signed out everywhere'], 'session.revoke': ['🚪', 'Device signed out'], 'device.create': ['🔑', 'Device key made'], export: ['⬇️', 'Data exported'] };
async function logView() {
  let events = [], err = '';
  try {
    ({ events } = await call('/api/admin/users/audit?limit=300'));
  } catch (e) {
    err = e.message;
  }
  const row = (e) => {
    const [ic, label] = LOG_EV[e.event] || (String(e.event).startsWith('admin.') ? ['🛡️', 'Admin: ' + e.event.slice(6)] : ['•', e.event]);
    const who = e.user || e.login || e.target || '';
    return `<div class="au-user"><span class="au-av">${ic}</span><div class="who"><b>${esc(label)}</b>${who ? ` <span class="dim">@${esc(who)}</span>` : ''}${e.newDevice ? ' <span class="pill" style="--a:#ff9a3d">new device</span>' : ''}${e.by ? ` <span class="dim">by @${esc(e.by)}</span>` : ''}
      <div class="meta"><span>${esc(new Date(e.ts).toLocaleString())}</span>${e.ip ? `<span>${esc(e.ip)}</span>` : ''}${e.ua ? `<span>${esc(String(e.ua).slice(0, 60))}</span>` : ''}${e.device ? `<span>${esc(e.device)}</span>` : ''}</div></div></div>`;
  };
  app.innerHTML = `<div class="au-page">${adminTop}${adminTabs()}
    <p class="hint" style="margin:4px 4px 14px">Every sign-in, wrong password, password change and admin action, newest first. Stored encrypted on the server (data/audit.log). New sign-ins to your account also reach your phone.</p>
    <section class="au-card wide">${err ? `<div class="au-empty">${esc(err)}</div>` : events.length ? `<div class="au-list">${events.map(row).join('')}</div>` : '<div class="au-empty">Nothing yet.</div>'}</section></div>`;
  app.querySelector('.au-tabs').onclick = (e) => {
    const b = e.target.closest('[data-tab]');
    if (b) (adminTab = b.dataset.tab), adminView();
  };
}

// ---------- admin → 📦 Apps: native app releases (docs/NATIVE.md §6) ----------
async function appsView() {
  const { platforms, names, keep } = await call('/api/app/releases');
  const PF = ['mac', 'windows', 'android', 'ios'];
  const rel = (r) => `<div class="au-user"><span class="au-av">${APP_IC[r.platform]}</span>
    <div class="who"><b>${esc(r.version)}</b> <span class="dim">build ${esc(r.build)}</span> ${r.channel === 'beta' ? '<span class="pill" style="--a:#ff9a3d">beta</span>' : ''}${r.latestFor.map((c) => `<span class="pill" style="--a:#35e09a">latest ${esc(c)}</span>`).join('')}${r.mandatory ? '<span class="pill" style="--a:#ff5d73">mandatory</span>' : ''}${r.minBuild ? `<span class="pill" style="--a:#ffc23d">min build ${esc(r.minBuild)}</span>` : ''}
      <div class="meta"><span>${ago(r.publishedAt)}</span>${r.publishedBy ? `<span>by @${esc(r.publishedBy)}</span>` : ''}${r.size ? `<span>${fmtSize(r.size)}</span>` : ''}${r.sha256 ? `<span title="${esc(r.sha256)}">sha256 ${esc(r.sha256.slice(0, 12))}…</span>` : ''}</div>
      ${r.notes ? `<div class="dim" style="font-size:13.5px;margin-top:6px;white-space:pre-wrap">${esc(r.notes)}</div>` : ''}</div>
    <div class="acts"><a class="btn sm" href="${esc(/^(https?:|\/)/i.test(r.url || '') ? r.url : '#')}" ${r.file ? 'download' : 'target="_blank" rel="noopener"'}>⬇ ${r.file ? 'Download' : 'Open link'}</a><button class="btn sm danger" data-unpub="${esc(r.platform)}:${esc(r.build)}">Unpublish</button></div></div>`;
  app.innerHTML = `<div class="au-page">${adminTop}${adminTabs()}
    <p class="hint" style="margin:4px 4px 14px">The native apps are thin shells around this web app: web changes reach them instantly. Publish a new shell build here (or with <code>scripts/release.sh</code>); installed apps update themselves. The last ${keep} releases per platform and channel are kept.</p>
    ${PF.map((pf) => `<section class="au-card wide" style="margin-bottom:14px"><h3>${APP_IC[pf]} ${esc(names[pf])}</h3>${platforms[pf].length ? `<div class="au-list">${platforms[pf].map(rel).join('')}</div>` : '<div class="au-empty" style="padding:14px">No releases yet.</div>'}</section>`).join('')}
    <section class="au-card wide"><h3>⬆️ Publish a release</h3><p class="hint">Upload the installer/update file. The build number must be higher than the last one.</p>
      <form id="relf" novalidate>${errBox}
        <div class="au-btn-row" style="flex-wrap:wrap;gap:10px">
          <label class="field" style="flex:1;min-width:130px"><span>Platform</span><select class="input" name="platform">${PF.map((p) => `<option value="${p}">${esc(names[p])}</option>`).join('')}</select></label>
          <label class="field" style="flex:1;min-width:110px"><span>Version</span><input class="input" name="version" placeholder="1.4.0" required/></label>
          <label class="field" style="flex:1;min-width:90px"><span>Build</span><input class="input" name="build" inputmode="numeric" placeholder="12" required/></label>
          <label class="field" style="flex:1;min-width:110px"><span>Channel</span><select class="input" name="channel"><option value="stable">Stable</option><option value="beta">Beta</option></select></label>
        </div>
        <label class="field"><span>What's new<small>shown in the update banner</small></span><textarea class="input" name="notes" rows="2" maxlength="4000"></textarea></label>
        <label class="field"><span>File</span><input class="input" type="file" name="file" required/></label>
        <label class="au-check"><input type="checkbox" name="mandatory"/> Mandatory (apps must update before they can be used)</label>
        <button class="btn primary" type="submit">Publish</button></form></section></div>`;
  app.querySelector('.au-tabs').onclick = (e) => {
    const b = e.target.closest('[data-tab]');
    if (b) (adminTab = b.dataset.tab), adminView();
  };
  app.addEventListener('click', async function un(e) {
    const b = e.target.closest('[data-unpub]');
    if (!b) return;
    const [platform, build] = b.dataset.unpub.split(':');
    if (!confirm(`Unpublish ${platform} build ${build}? Its file is deleted.`)) return;
    app.removeEventListener('click', un);
    await call('/api/app/releases/unpublish', { platform, build: Number(build) }).catch((x) => toast('⚠️ ' + esc(x.message), 'err'));
    appsView();
  });
  const form = app.querySelector('#relf');
  wireForm(form, async (f) => {
    const file = form.querySelector('input[type=file]').files[0];
    if (!file) throw new Error('Pick a file first');
    const res = await fetch('/api/app/releases', { method: 'POST', credentials: 'same-origin', headers: { 'X-Platform': f.platform, 'X-Version': f.version.trim(), 'X-Build': f.build.trim(), 'X-Channel': f.channel, 'X-Notes': encodeURIComponent(f.notes || ''), 'X-File-Name': file.name.replace(/[^A-Za-z0-9._-]/g, '-'), 'X-Mandatory': f.mandatory ? '1' : '0', 'Content-Type': 'application/octet-stream' }, body: file });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || `Upload failed (${res.status})`);
    toast(`📦 Published ${esc(data.release.platform)} ${esc(data.release.version)}`, 'gold');
    appsView();
  });
}

// ---------- router ----------
async function boot() {
  clearTimeout(pollT);
  let me;
  try {
    me = await call('/api/auth/me');
  } catch (e) {
    app.innerHTML = `<div class="au-solo"><div style="font-size:56px">📡</div><h2>Can't reach Questline</h2><p>${esc(e.message)}</p><button class="btn" onclick="location.reload()">Try again</button></div>`;
    return;
  }
  const u = me.user;
  document.title = 'Questline · ' + ({ '/setup': 'Set up', '/signup': 'Sign up', '/account': 'Account', '/admin': 'Admin' }[path] || 'Sign in');
  if (me.demo) return demoView();
  if (me.setup?.needed) {
    if (path !== '/setup') history.replaceState(null, '', '/setup');
    return setupView(me);
  }
  if (path === '/setup') return (location.href = u ? '/' : '/login');
  if (u && u.status === 'pending') return pendingView(me);
  if (u && (u.status === 'blocked' || u.status === 'denied')) return blockedView(me);
  if (path === '/account') return u ? accountView(me) : (location.href = '/login?next=/account');
  if (path === '/admin') return u?.role === 'admin' ? adminView().catch((e) => toast('⚠️ ' + esc(e.message), 'err')) : (location.href = u ? '/' : '/login?next=/admin');
  if (u && !me.trustedLocal) return (location.href = NEXT);
  if (path === '/signup') return signupView();
  return loginView(me);
}
boot();
